import {
    ICellMetric,
    ICellMetricAggregator,
    ICellMetricAggregatorCalcLevel,
    ICellMetricNumberType,
    ICellMetricPushDownMethod,
    ICellMetricPushDownOptions,
    ICellMetricRenderOptions,
    ICellMetricUOM, ICellMetricUOMConverters
} from "./PA.HyperRectangle.IF";
import {WB_CubeSourceDataSourceRow, WB_DataObjectColumnRow} from "../PA.Core/PAdbSchemaRows";
import {AdditiveMetricAggr} from "./AdditiveMetricAggr";
import {WAvgMetricAggr} from "./WAvgMetricAggr";
import * as _ from "lodash"
import {IUOMPolicyOptions, UOM} from "../PA.MD.Services/PA.MD.Services.IF";
import * as AsyncLock from "async-lock";
import {UOMPolicyService} from "../PA.MD.Services/UOMPolicyService";

export interface IDataSourceMetricOptions {
    aliasId: string
  [p: string]: any
}

export class DataSourceColumnMetric
    implements
        ICellMetric
{

    private _lock: AsyncLock
    private _metricId: string;
    private _metricName: string;
    private _metricFieldName: string;
    private _metricSort: number
    private _metricGroup: string

    private _options: IDataSourceMetricOptions
    private _renderOptions: ICellMetricRenderOptions

    private _metricVisible: boolean
    private _metricReadOnly: boolean
    private _metricWriteBackEnabled: boolean
    private _bIsLeafLevelOnly: boolean
    private _aggregatorCreate: () => ICellMetricAggregator
    private _pushDownMethod: ICellMetricPushDownMethod
    private _pushDownOptions: ICellMetricPushDownOptions
    private _cellMetricUOM: ICellMetricUOM
    private _cellMetricUOMConverters: ICellMetricUOMConverters
    private _ds: WB_CubeSourceDataSourceRow
    private _dsColumn: WB_DataObjectColumnRow

    constructor(ds: WB_CubeSourceDataSourceRow,
                dsColumn: WB_DataObjectColumnRow)
    {
        const self = this
        this._ds = ds
        this._dsColumn = dsColumn
        this._lock = new AsyncLock()
        this._metricId = `${ds.cif.ALIAS_ID}_${dsColumn.cif.COLUMN_ID}`
        this._metricName = dsColumn.cif.COLUMN_NAME
        this._metricFieldName = dsColumn.cif.COLUMN_ID
        this._cellMetricUOM = undefined
        this._cellMetricUOMConverters = undefined
        this._metricSort = dsColumn.cif.METRIC_SORT
        this._metricGroup = dsColumn.cif.METRIC_GROUP
        let decimalCount = 2
        let displayFormat = dsColumn.cif.DISPLAYFORMAT.trim()

        if(!_.isEmpty(displayFormat)){
            let idxOf = displayFormat.indexOf(".")
            if(idxOf >= 0) {
                decimalCount = displayFormat.length - idxOf - 1
                this._renderOptions = {
                    decimalPlaces: decimalCount,
                    showThousandsSeperator: true,
                    numberType: ICellMetricNumberType.Decimal
                }
            }
            else if (idxOf < 0) {
                this._renderOptions = {
                    decimalPlaces: 0,
                    showThousandsSeperator: true,
                    numberType: ICellMetricNumberType.Decimal
                }
            }
        }


        this._options = {aliasId: ds.cif.ALIAS_ID }

        // True if there is no aggregation behavior
        // -- changed to false if aggregator is available
        this._bIsLeafLevelOnly = true
        this._aggregatorCreate = undefined

        // Is it visible in the HR, requires aggregator for readonly,
        this._metricVisible =  (dsColumn.cif.METRIC_CUBESOURCE_VISIBLE_IND == '1')
        this._metricReadOnly = (dsColumn.cif.METRIC_EDITABLE_IND !== '1')
        this._metricWriteBackEnabled = (dsColumn.cif.METRIC_WB_ENABLED_IND == '1')

        let aggregatorMethod = dsColumn.cif.METRIC_AGGR_METHOD
        switch (aggregatorMethod){
            case "Additive":
                this._aggregatorCreate = () => {
                    let rv = new AdditiveMetricAggr(self._metricId)
                    return rv
                }
                this._bIsLeafLevelOnly = false
                break;

            case "Average":
                this._aggregatorCreate = () => {
                    return new WAvgMetricAggr(self._metricId)
                }
                this._bIsLeafLevelOnly = false
                break;

            case "WeightedAverage":
                this._aggregatorCreate = () => {
                    if(_.isEmpty(dsColumn.cif.METRIC_AGGR_WEIGHTBY))
                        return new WAvgMetricAggr(self._metricId)
                    else
                        return new WAvgMetricAggr(self._metricId, `${ds.cif.ALIAS_ID}_${dsColumn.cif.METRIC_AGGR_WEIGHTBY}`,0)
                }
                this._bIsLeafLevelOnly = false
                break;

            default:
                //-- If there is no aggregation method defined, it's not visible in Presentation Layer
                this._metricVisible = false

        }
        let pushDownMethod = dsColumn.cif.METRIC_PUSHDOWN_METHOD
        switch (true) {
            case _.isEmpty(pushDownMethod):
            case pushDownMethod == 'None':
                if(!this._metricReadOnly){
                    console.log(`Pushdown method not set for editable metric ${this._metricId}`)
                    this._metricReadOnly = true
                    this._pushDownMethod = ICellMetricPushDownMethod.None
                }
                this._pushDownOptions = {}
                break;

            case (pushDownMethod == 'CopyDown'):
                this._pushDownMethod = ICellMetricPushDownMethod.CopyDown
                this._pushDownOptions = {}
                break;

            case (pushDownMethod == 'ScaleBy'):
                this._pushDownMethod = ICellMetricPushDownMethod.ScaleBy
                this._pushDownOptions = {}
                break;

            case (pushDownMethod == 'Reallocate'):
            case (pushDownMethod == 'AllocateBy') && (_.isEmpty(dsColumn.cif.METRIC_PUSHDOWN_ALLOCATEBY)):
                this._pushDownMethod = ICellMetricPushDownMethod.Reallocate
                this._pushDownOptions = {}
                break;

            case (pushDownMethod == 'AllocateBy'):
                this._pushDownMethod = ICellMetricPushDownMethod.AllocateBy
                this._pushDownOptions = {AllocateByMetricId: `${ds.cif.ALIAS_ID}_${dsColumn.cif.METRIC_PUSHDOWN_ALLOCATEBY}` }
                break;
            default:
                this._metricReadOnly = true
                this._pushDownMethod = ICellMetricPushDownMethod.None
                this._pushDownOptions = {}
        }

        // Determine the UOM when simple case, empty
        if(_.isEmpty(dsColumn.cif.UOM_NUM) && _.isEmpty(dsColumn.cif.UOM_DENOM)){
            this._cellMetricUOM = {
                NumeratorUOM:  {bUOM: false, UOMCode: "", bCurrency: false, CurrencyCode: ""},
                DenominatorUOM: {bUOM: false, UOMCode: "", bCurrency: false, CurrencyCode: ""},
            }
        }

        // Determine the UOM Converters
        this._cellMetricUOMConverters = {
            NumeratorUOMConverters: _.filter(dsColumn.cif.UOM_CONVERTER_NUM.split(","), (a) => !_.isEmpty(a)),
            DenominatorUOMConverter: _.filter(dsColumn.cif.UOM_CONVERTER_DENOM.split(","), (a) => !_.isEmpty(a))
        }
    }

    get MetricId(): string { return this._metricId }
    get MetricName(): string { return this._metricName }
    get MetricFieldName(): string { return this._metricFieldName }
    get Sort(): number { return this._metricSort }
    get Options(): IDataSourceMetricOptions { return this._options }

    get GroupName(): string { return this._metricGroup }
    get RenderOptions(): ICellMetricRenderOptions { return this._renderOptions }
    get SupportingMetricsIds(): string[] { return [] }

    get bIsLeafLevelOnly(): boolean { return this._bIsLeafLevelOnly }

    get AggregatorCalcLevel(): ICellMetricAggregatorCalcLevel { return ICellMetricAggregatorCalcLevel.Leaf }

    Aggregator(): ICellMetricAggregator { return this._aggregatorCreate();  }


    get PushDownMethod(): ICellMetricPushDownMethod  { return this._pushDownMethod }
    get PushDownOptions(): ICellMetricPushDownOptions { return this._pushDownOptions }

    get bIsHidden(): boolean { return !this._metricVisible}

    get bIsReadOnly(): boolean { return this._metricReadOnly }

    get bIsWriteBackEnabled(): boolean { return this._metricWriteBackEnabled }

    getMetricUOM(options?: IUOMPolicyOptions): ICellMetricUOM | Promise<ICellMetricUOM> {
        const self = this
        if(!_.isEmpty(this._cellMetricUOM))
            return this._cellMetricUOM

        return self._lock.acquire("UOM", async (done) => {
            let cellUOM: ICellMetricUOM = {
                NumeratorUOM: {bUOM: false, UOMCode: "", bCurrency: false, CurrencyCode: ""},
                DenominatorUOM: {bUOM: false, UOMCode: "", bCurrency: false, CurrencyCode: ""},
            }
            try {
                let dsC = self._dsColumn.cif

                async function setUOM(sUOM: string, sAmtType: string, targetUOM: UOM) {
                    if (_.isEmpty(sUOM)) return
                    let uomParts = sUOM.split(":")
                    let uomType = uomParts[0]
                    let uomValue = uomParts[1]

                    switch (uomType) {
                        case "UOM":
                            targetUOM.bUOM = true
                            targetUOM.UOMCode = uomValue
                            break;

                        case "Currency":
                            targetUOM.bCurrency = true
                            targetUOM.CurrencyCode = uomValue
                            break;

                        case "UOMPolicy":
                            let policy = UOMPolicyService.getUOMPolicy(uomValue)
                            if (!_.isEmpty(policy)) {
                                let uom = await policy.getUOM(options)
                                if (uom)
                                    _.forEach(uom, (value, key) => {
                                        (<any>targetUOM)[key] = value
                                    })
                            }
                            break;
                        default:
                            console.log(`Unknown UOM (${sUOM}) for Metric ${self._metricId}`)
                            break;
                    }
                }

                if (!_.isEmpty(dsC.UOM_NUM))
                    await setUOM(dsC.UOM_NUM, dsC.UOM_AMOUNT_TYPE_NUM, cellUOM.NumeratorUOM)

                if (!_.isEmpty(dsC.UOM_DENOM))
                    await setUOM(dsC.UOM_DENOM, dsC.UOM_AMOUNT_TYPE_DENOM, cellUOM.DenominatorUOM)
                self._cellMetricUOM = cellUOM
                done(undefined, cellUOM)
            } catch(err) {
                done(err)
            }
        })

    }

    getMetricUOMConverters(): ICellMetricUOMConverters  { return this._cellMetricUOMConverters }

}